import { handleApiResponse, queryKeys } from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import {
  desc,
  type Organization,
  organization,
  type PowerSyncDrizzleDB,
} from "@stockpilot/powersync";
import { type Accessor, createMemo } from "solid-js";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { activeScopeIdFor, setActiveScopeIdFor } from "@/features/organizations/active-org";

/** Switcher entry: a real org or the Personal workspace (id = userId). */
export interface OrgScope {
  /** Scope ID: real org ID, or the userId (Personal). This is the `organization_id`. */
  id: string;
  /** Display name. */
  name: string;
  /** `true` for the Personal workspace (not a real organization). */
  personal: boolean;
}

export interface ActiveOrgContext {
  /** User's real organizations, synced locally (membership). */
  orgs: Accessor<Organization[]>;
  /** Switcher entries: [Personal, ...orgs]. */
  scopes: Accessor<OrgScope[]>;
  /**
   * ACTIVE `organization_id` — the single source for the WMS scope. It is the
   * active org ID, or the userId when the active scope is Personal. Validated
   * against what exists locally (self-healing: falls back to Personal if the
   * pointer is stale). Every WMS query derives from here, so switching reactively
   * re-scopes catalog/warehouses/stock/operations/reports.
   */
  activeScopeId: Accessor<string>;
  /** Active organization, or `null` if the active scope is Personal. */
  activeOrg: Accessor<Organization | null>;
  /** Active scope (org or Personal) as a switcher entry. */
  activeScope: Accessor<OrgScope>;
  /** Changes the active scope (persists per user; re-scopes the entire WMS). */
  setActiveOrg: (scopeId: string) => void;
  /**
   * Creates an organization (atomic server-side org+member, CC-3), waits for it
   * to sync locally, makes it ACTIVE, and returns its ID. This is the path for
   * both the FIRST supermarket/company (onboarding) and additional ones.
   */
  bootstrapOrg: (name: string) => Promise<string>;
}

/**
 * Multi-organization WMS context: the active company/supermarket and the user's
 * set of companies.
 *
 * Org-centric model: each organization is a company; its warehouses and
 * locations live inside it (`warehouse`/`location` entities with
 * `organization_id`). A user may belong to N orgs (the `organizations` sync
 * stream downloads `organization`/`member`); each org's inventory downloads via
 * `inventory_org` (`organization_id IN my_org_ids`). The Personal workspace
 * (F1) is the inventory with `organization_id = user_id`, downloaded via
 * `inventory_personal` — offered as the default scope so it does not break
 * existing data or guest mode.
 *
 * The active scope is a preference persisted per user (`active-org.ts`) and
 * validated against local orgs; every query derives from `activeScopeId()`, so
 * switching reactively re-scopes the entire WMS.
 */
export function useActiveOrg(): ActiveOrgContext {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();

  const orgsQuery = createPowerSyncQuery<Organization>({
    queryKey: () => queryKeys.inventory.memberships(),
    queryFn: () => {
      const d = drizzle();
      if (!d) {
        return null;
      }
      return d.select().from(organization).orderBy(desc(organization.created_at));
    },
    db: () => db(),
  });

  const orgs = createMemo<Organization[]>(() => orgsQuery.data ?? []);

  const scopes = createMemo<OrgScope[]>(() => [
    { id: session.userId(), name: "Personal", personal: true },
    ...orgs().map((o) => ({ id: o.id, name: o.name ?? o.slug ?? o.id, personal: false })),
  ]);

  const activeScopeId = createMemo<string>(() => {
    const uid = session.userId();
    // Persisted preference validated against what exists locally. Personal
    // (scope === userId) is always valid; if the pointer references an org that
    // is no longer local (deleted / not synced / stale data), fall back to
    // Personal — it never blocks (self-healing).
    const preferred = activeScopeIdFor(uid);
    if (!preferred || preferred === uid) {
      return uid;
    }
    return orgs().some((o) => o.id === preferred) ? preferred : uid;
  });

  const activeOrg = createMemo<Organization | null>(
    () => orgs().find((o) => o.id === activeScopeId()) ?? null,
  );

  const activeScope = createMemo<OrgScope>(() => {
    const org = activeOrg();
    if (org) {
      return { id: org.id, name: org.name ?? org.slug ?? org.id, personal: false };
    }
    return { id: session.userId(), name: "Personal", personal: true };
  });

  const setActiveOrg = (scopeId: string): void => {
    setActiveScopeIdFor(session.userId(), scopeId);
  };

  async function bootstrapOrg(name: string): Promise<string> {
    const d = drizzle();
    if (!d) {
      throw new Error("Local database is not ready");
    }
    // Local orgs may already exist: capture the previous IDs so we wait for the
    // NEW org (not "any org", which another row would satisfy immediately).
    const before = new Set(orgs().map((existing) => existing.id));
    const trimmed = name.trim();
    const slug =
      trimmed
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
        .replaceAll(/^-+|-+$/g, "")
        .slice(0, 40) || `org-${Date.now()}`;
    // `openapi-fetch` does NOT throw on 4xx/5xx (it returns `{error}`): without
    // this wrapper a 401 (guest without an account creating an org) would die
    // silently and the poll below would wait 30 s for an org that never arrives
    // — the "Create" button would stick without feedback (CC-30 / CC-8 family).
    // `handleApiResponse` throws on non-2xx and the switcher renders the error
    // inline.
    await handleApiResponse(
      api.POST("/api/org/createOrganization", {
        body: { name: trimmed, slug },
      }),
    );
    // The response only confirms `success`; the ID arrives when the org syncs
    // locally (the server atomically creates org+member, CC-3).
    const orgId = await waitForNewOrgLocal(d, before);
    if (!orgId) {
      throw new Error("Organization did not sync to local in time");
    }
    // The newly created org becomes active (onboarding: it was the only one;
    // additional creation: the user just requested it — switching is expected).
    setActiveOrg(orgId);
    return orgId;
  }

  return {
    orgs,
    scopes,
    activeScopeId,
    activeOrg,
    activeScope,
    setActiveOrg,
    bootstrapOrg,
  };
}

const POLL_INTERVAL_MS = 200;
const MAX_WAIT_MS = 30_000;

/**
 * Waits (using local polling) for an organization that was NOT in `before` to
 * appear and returns its ID (the most recent of the new ones).
 */
async function waitForNewOrgLocal(
  d: PowerSyncDrizzleDB,
  before: ReadonlySet<string>,
): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    const rows = await d.select().from(organization).orderBy(desc(organization.created_at));
    const fresh = rows.find((row) => !before.has(row.id));
    if (fresh?.id) {
      return fresh.id;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
